import type { ReactNode } from 'react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { TooltipProvider } from '@/components/ui/tooltip';

/**
 * Shared authenticated chrome — TooltipProvider + SidebarProvider + top header
 * + scrollable main. Every signed-in surface (workspace, organization,
 * super-admin) renders through this so the sidebar slot, header line, and
 * content padding stay identical instead of being copy-pasted per layout.
 *
 * - `header` is required — each surface injects its own <AppHeader>.
 * - `beforeChrome` mounts inside TooltipProvider, BEFORE SidebarProvider
 *   (welcome modal, announcement banner).
 * - `afterChrome` mounts inside TooltipProvider, AFTER/outside SidebarProvider
 *   (connect-channel modal).
 */
export function AppShell({
  sidebar,
  header,
  children,
  beforeChrome,
  afterChrome,
}: {
  sidebar: ReactNode;
  header: ReactNode;
  children: ReactNode;
  beforeChrome?: ReactNode;
  afterChrome?: ReactNode;
}) {
  return (
    <TooltipProvider>
      {beforeChrome}
      <SidebarProvider>
        {sidebar}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {header}
          <main className="flex-1 min-h-0 overflow-y-auto p-6 no-visible-scrollbar">
            {children}
          </main>
        </div>
      </SidebarProvider>
      {afterChrome}
    </TooltipProvider>
  );
}
